/**
 * TanStack Query key factories — single source of truth.
 *
 * Every query and mutation in the app must derive its key through this
 * module. Centralising the shapes here guarantees that:
 *
 *   1. Cache reads and cache invalidations always agree on the same key,
 *      so `queryClient.invalidateQueries({ queryKey: ['posts'] })` cascades
 *      to every posts-related query (list, single, related, prev, next).
 *   2. Structurally-equal inputs produce structurally-equal keys and
 *      structurally-different inputs produce structurally-different keys
 *      regardless of the caller's property insertion order — the invariant
 *      exercised by Property 13 (Query key determinism), whose property
 *      test lives in task 12.2 and targets the factories exported here.
 *   3. Keys stay stable across refactors: consumers depend on `keys.post`,
 *      not on a hand-written literal tuple.
 *
 * Design reference: `.kiro/specs/headless-cms-platform/design.md`
 * → State Management Design → Query key conventions.
 *
 * The imported domain types are used only in JSDoc `@link` references so
 * hovering over a factory in the editor jumps straight to the shape it
 * keys; they intentionally carry no runtime footprint (`import type`).
 */

import type {
  Page,
  Post,
  Category,
  Tag,
  Author,
  Media,
  Menu,
  SiteSettings,
  SearchResult,
} from '@/types';

/**
 * Return a shallow copy of `input` with every entry whose value is
 * `undefined` removed. Values that are `null`, `0`, `''`, or an empty
 * array are preserved because they represent explicit filter choices.
 *
 * TanStack Query hashes keys with a comparator that sorts object keys,
 * so the sole responsibility of this helper is to make sure two calls
 * like `keys.posts({ page: 1 })` and `keys.posts({ page: 1, perPage: undefined })`
 * produce structurally-equal keys.
 */
function pruneUndefined(input: object): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(input)) {
    if (value !== undefined) {
      out[key] = value;
    }
  }
  return out;
}

/** Filter and pagination inputs for a {@link Post} listing. */
export interface PostsListParams {
  readonly page?: number;
  readonly perPage?: number;
  readonly categories?: readonly number[];
  readonly tags?: readonly number[];
  readonly search?: string;
}

/** Selection inputs for the related-{@link Post} algorithm. */
export interface RelatedPostsParams {
  readonly postId: number;
  readonly categoryIds: readonly number[];
  readonly tagIds: readonly number[];
  readonly limit: number;
}

/** Lookup shape for taxonomy terms ({@link Category} / {@link Tag}). */
export interface TaxonomyLookup {
  readonly id?: number;
  readonly slug?: string;
}

/** Input for a {@link SearchResult} query. */
export interface SearchParams {
  readonly q: string;
  readonly type?: 'post' | 'page';
  readonly perPage?: number;
}

export const keys = {
  /** Key for a single {@link Page} fetched by slug. */
  page: (slug: string) => ['pages', { slug }] as const,

  /** Key for a single {@link Post} fetched by slug. */
  post: (slug: string) => ['posts', { slug }] as const,

  /**
   * Key for a paginated {@link Post} listing.
   *
   * `undefined` entries are stripped from the params object so callers
   * may pass sparse option bags without fracturing the cache.
   */
  posts: (params: PostsListParams = {}) => {
    const clean = pruneUndefined(params);
    return ['posts', 'list', clean] as const;
  },

  /**
   * Key for the related-{@link Post} selection anchored on a source post.
   *
   * All four selection inputs (postId, categoryIds, tagIds, limit) are
   * required; the prune step is defensive against callers who pass an
   * explicit `undefined`.
   */
  related: (input: RelatedPostsParams) => {
    const clean = pruneUndefined(input);
    return ['posts', 'related', clean] as const;
  },

  /** Key for the previous {@link Post} relative to `dateGmt`. */
  prev: (dateGmt: string) => ['posts', 'prev', { dateGmt }] as const,

  /** Key for the next {@link Post} relative to `dateGmt`. */
  next: (dateGmt: string) => ['posts', 'next', { dateGmt }] as const,

  /**
   * Key for a {@link Category} looked up by id or slug.
   *
   * Callers pass whichever identifier they hold; the prune step keeps
   * `{ id: 5 }` and `{ id: 5, slug: undefined }` structurally equal.
   */
  category: (input: TaxonomyLookup) => {
    const clean = pruneUndefined(input);
    return ['categories', clean] as const;
  },

  /** Key for a {@link Tag} looked up by id or slug. */
  tag: (input: TaxonomyLookup) => {
    const clean = pruneUndefined(input);
    return ['tags', clean] as const;
  },

  /** Key for a single {@link Author} fetched by id. */
  author: (id: number) => ['authors', { id }] as const,

  /** Key for a single {@link Media} item fetched by id. */
  media: (id: number) => ['media', { id }] as const,

  /** Key for a {@link Menu} tree fetched by theme location. */
  menu: (location: string) => ['menu', { location }] as const,

  /** Key for the {@link SiteSettings} singleton payload. */
  settings: () => ['settings'] as const,

  /**
   * Key for a {@link SearchResult} listing.
   *
   * Only `q` is required; `type` and `perPage` are pruned when omitted
   * so `keys.search({ q: 'foo' })` matches
   * `keys.search({ q: 'foo', type: undefined, perPage: undefined })`.
   */
  search: (input: SearchParams) => {
    const clean = pruneUndefined(input);
    return ['search', clean] as const;
  },
} as const;

/** Convenience aliases for the tuple types produced by each factory. */
export type PageKey = ReturnType<typeof keys.page>;
export type PostKey = ReturnType<typeof keys.post>;
export type PostsListKey = ReturnType<typeof keys.posts>;
export type RelatedPostsKey = ReturnType<typeof keys.related>;
export type PrevPostKey = ReturnType<typeof keys.prev>;
export type NextPostKey = ReturnType<typeof keys.next>;
export type CategoryKey = ReturnType<typeof keys.category>;
export type TagKey = ReturnType<typeof keys.tag>;
export type AuthorKey = ReturnType<typeof keys.author>;
export type MediaKey = ReturnType<typeof keys.media>;
export type MenuKey = ReturnType<typeof keys.menu>;
export type SettingsKey = ReturnType<typeof keys.settings>;
export type SearchKey = ReturnType<typeof keys.search>;
